/* @flow */

import React from 'react';

import LoadingComponent from 'components/loading';
import Reset2FAViewComponent from './view';

import Reset2FAForm from './model';

import UserService from 'services/UserService';
import loadConstraints from 'lib/validation/loadConstraints';
import {validateField} from 'lib/validation/validator';
import NotificationHelper from 'lib/notifications';
import {ApiValidationError} from 'services/api/apiErrors';

import {history} from 'store/index';
import {SIGN_IN} from 'constants/pageRoutes';

type Reset2FAComponentState = {
  constraints: any;
  errors: Map<string, ?string>;
  form: Reset2FAForm;
  formErrors: string[];
  isLoading: boolean;
  isProcessing: boolean;
};

export default class Reset2FAComponent extends React.Component<{}, Reset2FAComponentState> {

  constructor(props) {
    super(props);
    this.state = {
      constraints: null,
      errors: new Map([['company', undefined], ['email', undefined], ['password', undefined]]),
      form: new Reset2FAForm(),
      formErrors: [],
      isLoading: true,
      isProcessing: false
    };
  }

  componentDidMount = async () => {
    try {
      const constraints = await loadConstraints('Person', ['email']);
      this.setState({constraints, isLoading: false});
    } catch (e) {
      console.error(e);
      this.setState({isLoading: false});
    }
  };

  handleCompanySelect = (company) => {
    const form = this.state.form;
    form.company = company;
    const errors = this.state.errors;
    errors.set('company', company ? null : undefined);
    this.setState({form, errors});
  };

  handleEmailChange = (event) => {
    const value = event.target.value;
    const form = this.state.form;
    form.email = value;
    const errors = this.state.errors;
    const result = validateField(value, this.state.constraints.email);
    errors.set('email', result ? result[0] : null);
    this.setState({form, errors});
  };

  handlePasswordChange = (event) => {
    const value = event.target.value;
    const form = this.state.form;
    form.password = value;
    const errors = this.state.errors;
    errors.set('password', value ? null : 'twofactor.reset.password.required');
    this.setState({form, errors});
  };

  validate = () => {
    const errors = this.state.errors;
    let isValid = true;

    if (!this.state.form.company) {
      errors.set('company', 'twofactor.reset.company.required');
      isValid = false;
    }

    const emailResult = validateField(this.state.form.email, this.state.constraints.email);
    if (emailResult) {
      errors.set('email', emailResult[0]);
      isValid = false;
    }

    if (!this.state.form.password) {
      errors.set('password', 'twofactor.reset.password.required');
      isValid = false;
    }

    this.setState({errors});
    return isValid;
  };

  submitForm = async () => {
    if (!this.validate()) return;

    this.setState({formErrors: [], isProcessing: true});
    try {
      const request = this.state.form.toRequest();
      await UserService.requestLoginTwoFactorReset(request.companyId, request.email, request.password);
      NotificationHelper.createNotification(NotificationHelper.TYPE_SUCCESS, 'twofactor.reset.success');
      history.push(SIGN_IN);
    } catch (e) {
      if (e instanceof ApiValidationError) {
        // Wrong email/password (or the user is not a member of the chosen company).
        this.setState({formErrors: ['appModule.signInFailed'], isProcessing: false});
      } else {
        NotificationHelper.createNotification(NotificationHelper.TYPE_ERROR);
        this.setState({isProcessing: false});
      }
    }
  };

  render() {
    if (this.state.isLoading) {
      return (<LoadingComponent />);
    }

    return (
      <Reset2FAViewComponent
        errors={this.state.errors}
        form={this.state.form}
        formErrors={this.state.formErrors}
        isProcessing={this.state.isProcessing}
        handleCompanySelect={this.handleCompanySelect}
        handleEmailChange={this.handleEmailChange}
        handlePasswordChange={this.handlePasswordChange}
        submitForm={this.submitForm}
      />
    );
  }
}
